import React, { useEffect, useState } from "react";

import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { LuAlertCircle, LuRuler } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getSizeUnit,
  setSizeUnit,
} from "../../features/appSettings/appSettingsSlice";
import { PUT } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";

interface IFormInput {
  sizeUnit: string;
}

const SizeUnitForm = () => {
  const dispatch = useAppDispatch();
  const sizeUnit = useAppSelector(getSizeUnit);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    mode: "onSubmit",
    defaultValues: { sizeUnit: "mm" },
  });

  const previewUnit = (watch("sizeUnit") || "").trim();

  useEffect(() => {
    reset({ sizeUnit });
  }, [sizeUnit, reset]);

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);

    try {
      await PUT("/v1/admin/settings/size-unit", {
        sizeUnit: data.sizeUnit.trim(),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            const saved = response.data.data.sizeUnit || "mm";

            dispatch(setSizeUnit(saved));
            reset({ sizeUnit: saved });

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight flex items-center gap-2">
          <LuRuler className="h-6 w-6 text-[#34699A]" />
          સાઈઝ યુનિટ
        </div>
        <div className="text-sm text-gray-600">
          મટીરીયલ માં સાઈઝ પછી દેખાતો યુનિટ, જેમ કે 3600mm.
        </div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="sizeUnit-sizeUnit"
                className="text-sm font-semibold text-[#113F67]"
              >
                યુનિટ નું નામ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuRuler className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="sizeUnit-sizeUnit"
                  type="text"
                  maxLength={10}
                  placeholder="દા.ત. mm, cm, m"
                  {...register("sizeUnit", {
                    validate: (value) => value.trim().length > 0,
                  })}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.sizeUnit
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
                {errors.sizeUnit && (
                  <LuAlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.sizeUnit ? (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  સાઈઝ યુનિટ દાખલ કરો
                </p>
              ) : (
                <p className="text-gray-500 text-sm mt-1">
                  ઉદાહરણ: 3600{previewUnit}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  સેવ થઈ રહ્યું છે...
                </div>
              ) : (
                "સાચવો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default SizeUnitForm;
